// LoginSignup.js - CORRIGÉ pour gérer le token dans l'URL
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { ApiContext } from '../App';
import cognitoIcon from '../Assets/cognito-icon.png'
import { AppContent } from '../context/AppContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEye, FaEyeSlash, FaGoogle } from 'react-icons/fa';

const LoginSignup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const API = useContext(ApiContext);
  const { setIsLoggedin, getUserData } = useContext(AppContent);
  const [state, setState] = useState("Login");
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: ""
  });

  const { setUser, getUserData: getAuthUserData } = useAuth();

  // 🔧 CORRIGÉ: Google Auth Handler avec support du token URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const authStatus = urlParams.get('auth');
    const tokenFromUrl = urlParams.get('token');
    const error = urlParams.get('error');

    if (authStatus === 'success') {
      console.log('🎉 Google auth success detected');
      setIsGoogleAuthLoading(true);
      
      // Si on a un token dans l'URL, l'utiliser directement
      if (tokenFromUrl) {
        console.log('🔑 Token found in URL, verifying...');
        handleTokenVerification(tokenFromUrl);
      } else {
        // Sinon, vérifier la session
        handleGoogleAuthSuccess();
      }
      
    } else if (error) {
      console.error('❌ Google auth error:', error);
      handleGoogleAuthError(error);
    }
  }, [location]);

  // 🔧 NOUVELLE FONCTION: Vérifier le token depuis l'URL
  const handleTokenVerification = async (token) => {
    try {
      console.log('🔍 Verifying token from URL...');
      
      const response = await API.get(`/auth/verify-token/${token}`);
      
      if (response.data.success && response.data.user) {
        console.log('✅ Token verified, user:', response.data.user.email);
        
        // Stocker le token dans localStorage
        localStorage.setItem('auth-token', token);
        
        // Mettre à jour l'état de l'application
        setUser(response.data.user);
        setIsLoggedin(true);

        // Récupérer les données utilisateur complètes (AuthContext pour la photo)
        await getAuthUserData();

        // Récupérer aussi les données AppContext si nécessaire
        getUserData();

        toast.success('Successfully logged in with Google!', {
          position: "top-right",
          autoClose: 3000,
        });

        // Nettoyer l'URL et naviguer
        navigate("/app", { replace: true });
        
      } else {
        throw new Error('Token verification failed');
      }
      
    } catch (error) {
      console.error('❌ Error verifying token:', error);
      toast.error('Authentication verification failed. Please try again.');
      navigate('/login', { replace: true });
    } finally {
      setIsGoogleAuthLoading(false);
    }
  };

  // Handle successful Google authentication (fallback method)
  const handleGoogleAuthSuccess = async () => {
    try {
      console.log('🔍 Verifying Google authentication via session...');
      
      const response = await API.get('/auth/current_user', {
        withCredentials: true
      });
      
      if (response.data.success && response.data.user) {
        console.log('✅ Google user verified via session:', response.data.user.email);
        
        // Store token if provided
        if (response.data.token) {
          localStorage.setItem('auth-token', response.data.token);
        }
        
        // Update app state
        setUser(response.data.user);
        setIsLoggedin(true);

        // Get full user data (AuthContext pour la photo)
        await getAuthUserData();

        // Récupérer aussi les données AppContext si nécessaire
        getUserData();

        toast.success('Successfully logged in with Google!', {
          position: "top-right",
          autoClose: 3000,
        });

        // Clean URL and navigate
        navigate("/app", { replace: true });
        
      } else {
        throw new Error('No user data received from backend');
      }
      
    } catch (error) {
      console.error('❌ Error verifying Google auth via session:', error);
      
      // More specific error handling
      if (error.response?.status === 401) {
        toast.error('Authentication session expired. Please try again.');
      } else if (error.response?.status === 404) {
        toast.error('Authentication service not found. Please contact support.');
      } else {
        toast.error('Authentication verification failed. Please try again.');
      }
      
      navigate('/login', { replace: true });
    } finally {
      setIsGoogleAuthLoading(false);
    }
  };

  // Handle Google authentication errors
  const handleGoogleAuthError = (error) => {
    let errorMessage = 'Authentication failed';
    
    switch (error) {
      case 'google_auth_failed':
        errorMessage = 'Google authentication failed. Please try again.';
        break;
      case 'callback_error':
        errorMessage = 'Authentication error occurred. Please try again.';
        break;
      case 'no_user':
        errorMessage = 'No user data received from Google.';
        break;
      default:
        errorMessage = `Authentication failed: ${error}`;
    }
    
    toast.error(errorMessage);
    navigate('/login', { replace: true });
  };

  // Handle form input changes
  const changeHandler = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Google OAuth initiation
  const handleGoogleAuth = () => {
    console.log('🚀 Starting Google OAuth flow');
    setIsGoogleAuthLoading(true);
    
    // Get backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 
                      (process.env.NODE_ENV === 'development' 
                        ? 'http://localhost:5000' 
                        : 'https://cognito-back-production.up.railway.app');
        
    // Redirect to Google OAuth endpoint
    const oauthUrl = `${backendUrl}/auth/google`;    
    window.location.href = oauthUrl;
  };

  // Traditional login function
  const login = async () => {
    try {
      const response = await API.post('/api/login', formData);
      const responseData = response.data;

      if (responseData.success) {
        const token = responseData.token;
        localStorage.setItem('auth-token', token);

        toast.success('Successfully logged in!', {
          position: "top-right",
          autoClose: 3000,
        });

        setUser(token);
        setIsLoggedin(true);

        // Appeler getUserData du AuthContext pour charger la photo et les infos user
        await getAuthUserData();

        // Appeler aussi getUserData du AppContext si nécessaire
        getUserData();

        navigate("/app");
      } else {
        toast.error(responseData.message || 'Login failed');
      }
    } catch (error) {
      console.error("Login error:", error);

      if (error.response?.status === 401) {
        toast.error("Invalid email or password");
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error("Login failed. Please check your connection.");
      }
    }
  };

  // Traditional signup function
  const signup = async () => {
    try {
      if (!formData.username || !formData.email || !formData.password) {
        toast.error('Name, email, and password are required.');
        return;
      }

      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters long.');
        return;
      }

      const response = await API.post('/api/signup', formData);
      const responseData = response.data;

      if (responseData.success) {
        const token = responseData.token;
        localStorage.setItem('auth-token', token);

        toast.success('Account Successfully Created', {
          position: "top-right",
          autoClose: 3000,
        });

        setUser(token);
        setIsLoggedin(true);

        // Appeler getUserData du AuthContext pour charger la photo et les infos user
        await getAuthUserData();

        // Appeler aussi getUserData du AppContext si nécessaire
        getUserData();

        navigate("/app");
      } else {
        toast.error(responseData.message || 'Signup failed');
      }
    } catch (error) {
      console.error("Signup error:", error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 400) {
        toast.error("Invalid signup data. Please check your information.");
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error("Signup failed. Please check your connection.");
      }
    }
  };

  // Loading state for Google auth
  if (isGoogleAuthLoading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-indigo-100 to-indigo-300 flex items-center justify-center'>
        <div className='bg-white p-8 rounded-xl shadow-lg'>
          <div className='text-center'>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-4"></div>
            <p className='text-slate-700'>Completing Google authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-indigo-100 to-indigo-300 p-8'>
      <div onClick={() => navigate('/')} className='flex items-center gap-3 cursor-pointer'>
        <img src={cognitoIcon} alt="Cognito Logo" className="w-12 h-12 justify-center items-center" />
        <h1 className='text-slate-900 m-0 font-bold'>Jobrise</h1>
      </div>

      <div className='flex items-center justify-center'>
        <div className='flex items-center justify-center min-h-[calc(100vh-132px)]'>
          <div className='bg-white p-8 rounded-xl shadow-lg w-96 mt-8'>
            
            <div className='mb-3'>
              <h1 className='text-2xl font-semibold text-center mb-6'>{state}</h1>
            </div>

            {/* Google OAuth Button */}
            <div className='mb-6'>
              <button
                onClick={handleGoogleAuth}
                disabled={isGoogleAuthLoading}
                className={`w-full flex items-center justify-center gap-3 bg-white border border-slate-300 text-slate-700 py-2 px-4 rounded hover:bg-gray-50 transition duration-300 shadow-sm ${
                  isGoogleAuthLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <FaGoogle className='text-red-500' />
                {isGoogleAuthLoading 
                  ? "Connecting..." 
                  : (state === "Sign Up" ? "Sign up with Google" : "Continue with Google")
                }
              </button>
              
              {/* OR Separator */}
              <div className='flex items-center my-4'>
                <div className='flex-1 border-t border-slate-300'></div>
                <span className='px-3 text-slate-500 text-sm'>OR</span>
                <div className='flex-1 border-t border-slate-300'></div>
              </div>
            </div>
          
            {/* Traditional Form */}
            <div className='space-y-4'>
              {state === "Sign Up" && (
                <input
                  name='username'
                  value={formData.username}
                  onChange={changeHandler}
                  type="text"
                  placeholder='Your Name'
                  className='w-full p-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500'
                  required
                />
              )}
              <input
                name='email'
                value={formData.email}
                onChange={changeHandler}
                type="email"
                placeholder='Email'
                className='w-full p-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500'
                required
              />
              
              <div className="relative">
                <input
                  name='password'
                  value={formData.password}
                  onChange={changeHandler}
                  type={showPassword ? "text" : "password"}
                  placeholder='Password'
                  className='w-full p-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500'
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <p onClick={() => navigate('/reset-password')} className='text-indigo-500 cursor-pointer mt-2 text-sm hover:underline'>
              Forgot your password?
            </p>

            <button
              onClick={() => { state === "Login" ? login() : signup() }}
              className='w-full bg-indigo-500 text-white py-2 mt-4 rounded hover:bg-indigo-600 transition duration-300 disabled:opacity-50'
              disabled={isGoogleAuthLoading}
            >
              Continue
            </button>

            {/* Toggle between Login/Signup */}
            {state === "Sign Up" ? (
              <p className='text-center mt-4'>
                Already have an account?{' '}
                <span
                  onClick={() => { setState("Login") }}
                  className='text-blue-500 cursor-pointer underline hover:text-blue-600'
                >
                  Login here
                </span>
              </p>
            ) : (
              <p className='text-center mt-4'>
                Create an account?{' '}
                <span
                  onClick={() => { setState("Sign Up") }}
                  className='text-blue-500 cursor-pointer underline hover:text-blue-600'
                >
                  Click here
                </span>
              </p>
            )}

            <div className='flex items-center mt-4'>
              <input type="checkbox" className='mr-2' id="terms" />
              <label htmlFor="terms" className='text-sm text-slate-600 cursor-pointer'>
                By continuing, I agree to the terms of use & privacy policy.
              </label>
            </div>

            {/* Link to new app version */}
            <div className='mt-6 pt-4 border-t border-slate-200 text-center'>
              <p className='text-sm text-slate-600'>
                Accéder à la nouvelle version de l'application ?{' '}
                <a
                  href="https://app.jobrise.tech/login"
                  className='text-indigo-500 font-medium hover:text-indigo-600 hover:underline'
                >
                  Cliquez ici
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSignup;